import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { GoogleButton } from '@/components/auth/GoogleButton';
import { GitHubButton } from '@/components/auth/GitHubButton';
import { Turnstile } from '@/components/auth/Turnstile';
import { useAuth } from '@/contexts/AuthContext';

interface InviteDetails {
  invite_id: string;
  project_id: string;
  project_name: string;
  invite_email: string | null;
  role: string;
  inviter_name: string | null;
  has_existing_account: boolean;
}

export function InviteSignupPage() {
  const { inviteId, secret } = useParams<{ inviteId: string; secret: string }>();
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const { signup } = useAuth();

  const turnstileSiteKey = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY || '0x4AAAAAACAXLVY6peIBHA7D';

  useEffect(() => {
    async function fetchInvite() {
      try {
        const response = await fetch(`/api/invite/${inviteId}/${secret}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          const data = await response.json();
          setInviteError(data.error || 'Invalid invitation link');
          setLoading(false);
          return;
        }

        const data = await response.json();
        setInvite(data);
        if (data.invite_email) {
          setEmail(data.invite_email);
        }
      } catch {
        setInviteError('Failed to load invitation');
      } finally {
        setLoading(false);
      }
    }

    fetchInvite();
  }, [inviteId, secret]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      errors.confirm_password = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirm_password = 'Passwords do not match';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await signup({
        email,
        password,
        confirm_password: confirmPassword,
        cf_turnstile_response: turnstileToken,
        invite_id: inviteId,
        invite_secret: secret,
      });

      if (!result.success) {
        if (result.errors) {
          setFieldErrors(result.errors);
        } else {
          setError(result.error || 'An error occurred. Please try again.');
        }
        setIsSubmitting(false);
        return;
      }

      // Success - AuthContext handles the redirect to the project
    } catch {
      setError('An error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AuthLayout>
    );
  }

  if (inviteError || !invite) {
    return (
      <AuthLayout>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{inviteError || 'Invalid invitation'}</AlertDescription>
        </Alert>
        <div className="text-center">
          <Link to="/auth/login">
            <Button variant="outline">Go to Login</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* Invitation Banner */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Users className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <p className="font-medium text-gray-900">
              You're invited to join <span className="text-primary">{invite.project_name}</span>
            </p>
            {invite.inviter_name && (
              <p className="text-sm text-muted-foreground mt-1">
                Invited by {invite.inviter_name} as {invite.role}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Title */}
      <h1 className="text-xl font-semibold text-gray-900 mb-6">
        Create your account
      </h1>

      {/* Signup Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (fieldErrors.email) {
                setFieldErrors(prev => ({ ...prev, email: '' }));
              }
            }}
            className={fieldErrors.email ? 'border-red-500' : ''}
            required
            autoComplete="email"
          />
          {fieldErrors.email && (
            <p className="text-sm text-red-500">{fieldErrors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (fieldErrors.password) {
                setFieldErrors(prev => ({ ...prev, password: '' }));
              }
            }}
            className={fieldErrors.password ? 'border-red-500' : ''}
            required
            autoComplete="new-password"
          />
          {fieldErrors.password && (
            <p className="text-sm text-red-500">{fieldErrors.password}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm">Confirm Password</Label>
          <Input
            id="confirm"
            type="password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (fieldErrors.confirm_password) {
                setFieldErrors(prev => ({ ...prev, confirm_password: '' }));
              }
            }}
            className={fieldErrors.confirm_password ? 'border-red-500' : ''}
            required
            autoComplete="new-password"
          />
          {fieldErrors.confirm_password && (
            <p className="text-sm text-red-500">{fieldErrors.confirm_password}</p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full h-11"
          disabled={isSubmitting || (!!turnstileSiteKey && !turnstileToken)}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Creating account...
            </>
          ) : (
            'Create Account & Join Project'
          )}
        </Button>
      </form>

      {/* Turnstile Widget */}
      {turnstileSiteKey && (
        <Turnstile
          siteKey={turnstileSiteKey}
          onSuccess={(token) => setTurnstileToken(token)}
          onError={() => {
            setTurnstileToken(null);
            setError('Verification failed. Please try again.');
          }}
          onExpire={() => {
            setTurnstileToken(null);
            setError('Verification expired. Please try again.');
          }}
        />
      )}

      {/* Divider */}
      <div className="relative my-6">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-muted-foreground">
          or continue with
        </span>
      </div>

      {/* OAuth Buttons */}
      <div className="space-y-3">
        <GoogleButton
          text="Sign up with Google"
          disabled={!!turnstileSiteKey && !turnstileToken}
        />
        <GitHubButton
          text="Sign up with GitHub"
          disabled={!!turnstileSiteKey && !turnstileToken}
        />
      </div>

      {/* Login Link */}
      <div className="mt-6 text-center">
        <Link
          to={`/auth/invite-login/${inviteId}/${secret}`}
          className="text-sm text-muted-foreground hover:text-primary"
        >
          Already have an account? Log in
        </Link>
      </div>
    </AuthLayout>
  );
}
